import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";

const API_URL = process.env.REACT_APP_API_URL;
const CURRENCY_OPTIONS = ["INR", "USD", "EUR"];

function parseApiResponse(raw) {
  let data = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return raw;
    }
  }
  if (data?.body != null) {
    if (typeof data.body === "string") {
      try {
        return JSON.parse(data.body);
      } catch {
        return data.body;
      }
    }
    return data.body;
  }
  return data;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const success = responseData.success;
  return (
    success === true ||
    success === "true" ||
    success === 1 ||
    success === "1"
  );
}

function displayValue(value) {
  if (value == null || value === "") return "-";
  return String(value);
}

function extractCustomersList(payload) {
  const data = payload?.data != null ? payload.data : payload;
  const list =
    (Array.isArray(data?.customers) && data.customers) ||
    (Array.isArray(data?.data) && data.data) ||
    (Array.isArray(payload?.customers) && payload.customers) ||
    (Array.isArray(data) && data) ||
    [];
  return list
    .map((item) => ({
      _id: String(item?._id || item?.id || "").trim(),
      customerName: String(
        item?.customerName || item?.customer || item?.name || "",
      ).trim(),
    }))
    .filter((item) => item._id && item.customerName)
    .sort((a, b) => a.customerName.localeCompare(b.customerName));
}

function getCustomerIdFromPriceRow(row) {
  const customerRef = row?.customerId;
  if (customerRef && typeof customerRef === "object") {
    return String(customerRef._id || customerRef.id || "").trim();
  }
  return String(customerRef || "").trim();
}

function getCustomerPriceRows(item) {
  const list = Array.isArray(item?.customerPrices) ? item.customerPrices : [];
  return list.map((row, index) => {
    const customerRef = row?.customerId;
    const customerId = getCustomerIdFromPriceRow(row);
    const customerName =
      String(row?.customerName || "").trim() ||
      (customerRef && typeof customerRef === "object"
        ? String(customerRef.customerName || "").trim()
        : "") ||
      "-";
    const currency = String(row?.Currency || row?.priceType || "").trim();
    const customerMaterialCode = String(
      row?.customerMaterialCode || "",
    ).trim();
    const customerMaterialDescription = String(
      row?.customerMaterialDescription || "",
    ).trim();
    const price =
      row?.price == null || row?.price === ""
        ? ""
        : Number.isFinite(Number(row.price))
          ? Number(row.price)
          : row.price;
    const deliveryLeadTime = String(row?.deliveryLeadTime ?? "").trim();
    const customerPriceId = String(row?._id || row?.id || "").trim();

    return {
      key: customerPriceId || String(index),
      customerPriceId,
      customerId,
      customerName,
      currency: currency || "INR",
      customerMaterialCode,
      customerMaterialDescription,
      price,
      deliveryLeadTime,

    };
  });
}

const ViewField = ({ label, children }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">{label}</label>
    <div className="col-12 col-md-8">
      <div className="txtdsply">{children}</div>
    </div>
  </div>
);

const FormField = ({ label, children, required = false }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">
      {label}
      {required ? <span className="text-danger"> *</span> : null}
    </label>
    <div className="col-12 col-md-8">{children}</div>
  </div>
);

const SectionTitle = ({ icon, title }) => (
  <div className="row mb-2 mt-3">
    <div className="col-12">
      <h5 className="text-primary border-bottom pb-2 mb-3">
        <i className={`fa-solid ${icon} mr-2`} />
        {title}
      </h5>
    </div>
  </div>
);

const EMPTY_FORM = {
  customerId: "",
  customerName: "",
  Currency: "INR",
  customerMaterialCode: "",
  customerMaterialDescription: "",
  price: "",
  deliveryLeadTime: "",

};

const EMPTY_EDIT_FORM = {
  Currency: "INR",
  customerMaterialCode: "",
  customerMaterialDescription: "",
  price: "",
  deliveryLeadTime: "",

};

function EcatalogueEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [item, setItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [isCustomersLoading, setIsCustomersLoading] = useState(true);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [editingRowId, setEditingRowId] = useState("");
  const [editForm, setEditForm] = useState({ ...EMPTY_EDIT_FORM });
  const [isUpdatingRow, setIsUpdatingRow] = useState(false);

  const fetchCustomers = useCallback(async () => {
    setIsCustomersLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-customers",
        search: "",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (isApiSuccess(responseData)) {
        setCustomers(extractCustomersList(responseData));
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error("Fetch customers error:", error);
      setCustomers([]);
    } finally {
      setIsCustomersLoading(false);
    }
  }, []);

  const fetchEcatalogueById = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!id) {
      setItem(null);
      setIsLoading(false);
      return;
    }

    if (!silent) setIsLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-ecatalogue-by-id",
        id,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const detail =
        responseData?.data && typeof responseData.data === "object"
          ? responseData.data
          : null;

      if (isApiSuccess(responseData) && detail) {
        setItem(detail);
        if (!silent) {
          const currency = String(
            detail.Currency || detail.priceType || "INR",
          ).trim();
          setFormData({
            customerId: "",
            customerName: "",
            Currency: CURRENCY_OPTIONS.includes(currency) ? currency : "INR",
            customerMaterialCode: "",
            customerMaterialDescription: "",
            price: "",
            deliveryLeadTime: "",
          });
        }
      } else {
        if (!silent) setItem(null);
        toast.error(
          responseData?.message || "Failed to fetch e-catalogue details",
          { autoClose: 2000 },
        );
      }
    } catch (error) {
      console.error("Fetch e-catalogue by id error:", error);
      if (!silent) setItem(null);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        (typeof errData === "object" && errData?.message) ||
          "An error occurred while fetching e-catalogue details",
        { autoClose: 2000 },
      );
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    fetchEcatalogueById();
  }, [fetchEcatalogueById]);

  useEffect(() => {
    const selectedId = String(formData.customerId || "").trim();
    if (!selectedId || !item) return;
    const alreadyAdded = (Array.isArray(item.customerPrices)
      ? item.customerPrices
      : []
    ).some((row) => getCustomerIdFromPriceRow(row) === selectedId);
    if (alreadyAdded) {
      setFormData((prev) => ({
        ...prev,
        customerId: "",
        customerName: "",
      }));
    }
  }, [item, formData.customerId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCustomerChange = (e) => {
    const customerId = e.target.value;
    const alreadyAdded = (Array.isArray(item?.customerPrices)
      ? item.customerPrices
      : []
    ).some((row) => getCustomerIdFromPriceRow(row) === String(customerId).trim());
    if (customerId && alreadyAdded) {
      toast.error(
        "This customer is already added for this material code",
        { autoClose: 2000 },
      );
      setFormData((prev) => ({
        ...prev,
        customerId: "",
        customerName: "",
      }));
      return;
    }
    const selected = customers.find((c) => c._id === customerId);
    setFormData((prev) => ({
      ...prev,
      customerId,
      customerName: selected?.customerName || "",
    }));
  };

  const handleStartEditRow = (row) => {
    if (!row?.customerPriceId || isUpdatingRow) return;
    setEditingRowId(row.customerPriceId);
    setEditForm({
      Currency: CURRENCY_OPTIONS.includes(row.currency)
        ? row.currency
        : row.currency || "INR",
      customerMaterialCode: String(row.customerMaterialCode || ""),
      customerMaterialDescription: String(
        row.customerMaterialDescription || "",
      ),
      price: row.price === "-" || row.price === "" ? "" : String(row.price),
      deliveryLeadTime: String(row.deliveryLeadTime || "").trim(),
    });
  };

  const handleCancelEditRow = () => {
    if (isUpdatingRow) return;
    setEditingRowId("");
    setEditForm({ ...EMPTY_EDIT_FORM });
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveEditRow = async (row) => {
    if (!id || !row?.customerPriceId || isUpdatingRow) return;

    const updatedBy = localStorage.getItem("id");
    if (!updatedBy) {
      toast.error("User session not found. Please login again.", {
        autoClose: 2000,
      });
      return;
    }

    const currency = String(editForm.Currency || "").trim();
    if (!currency) {
      toast.error("Currency is required", { autoClose: 2000 });
      return;
    }

    const priceRaw = String(editForm.price || "").trim();
    if (priceRaw === "") {
      toast.error("Price is required", { autoClose: 2000 });
      return;
    }

    const priceNumber = Number(priceRaw);
    if (!Number.isFinite(priceNumber) || priceNumber < 0) {
      toast.error("Enter a valid price", { autoClose: 2000 });
      return;
    }

    setIsUpdatingRow(true);
    try {
      const response = await axios.post(API_URL, {
        action: "update-ecatalogue",
        id,
        customerPriceId: row.customerPriceId,
        price: priceNumber,
        customerMaterialCode: String(
          editForm.customerMaterialCode || "",
        ).trim(),
        customerMaterialDescription: String(
          editForm.customerMaterialDescription || "",
        ).trim(),
        Currency: currency,
        deliveryLeadTime: String(editForm.deliveryLeadTime || "").trim() || null,
        updatedBy,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData?.message || "Customer price updated successfully",
          { autoClose: 2000 },
        );
        setEditingRowId("");
        setEditForm({ ...EMPTY_EDIT_FORM });
        await fetchEcatalogueById({ silent: true });
      } else {
        toast.error(
          responseData?.message || "Failed to update customer price",
          { autoClose: 2000 },
        );
      }
    } catch (error) {
      console.error("Update e-catalogue customer price error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        (typeof errData === "object" && errData?.message) ||
          "An error occurred while updating customer price",
        { autoClose: 2000 },
      );
    } finally {
      setIsUpdatingRow(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!id || isSubmitting) return;

    const updatedBy = localStorage.getItem("id");
    if (!updatedBy) {
      toast.error("User session not found. Please login again.", {
        autoClose: 2000,
      });
      return;
    }

    const customerId = String(formData.customerId || "").trim();
    const customerName = String(formData.customerName || "").trim();
    if (!customerId || !customerName) {
      toast.error("Customer is required", { autoClose: 2000 });
      return;
    }

    const alreadyAdded = (Array.isArray(item?.customerPrices)
      ? item.customerPrices
      : []
    ).some((row) => getCustomerIdFromPriceRow(row) === customerId);
    if (alreadyAdded) {
      toast.error(
        "This customer is already added for this material code. Choose a different customer.",
        { autoClose: 2500 },
      );
      return;
    }

    const currency = String(formData.Currency || "").trim();
    if (!currency) {
      toast.error("Currency is required", { autoClose: 2000 });
      return;
    }

    const priceRaw = String(formData.price || "").trim();
    if (priceRaw === "") {
      toast.error("Price is required", { autoClose: 2000 });
      return;
    }

    const priceNumber = Number(priceRaw);
    if (!Number.isFinite(priceNumber) || priceNumber < 0) {
      toast.error("Enter a valid price", { autoClose: 2000 });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(API_URL, {
        action: "add-ecatalogue",
        id,
        customerId,
        customerName,
        Currency: currency,
        customerMaterialCode: String(
          formData.customerMaterialCode || "",
        ).trim(),
        customerMaterialDescription: String(
          formData.customerMaterialDescription || "",
        ).trim(),
        price: priceNumber,
        deliveryLeadTime: String(formData.deliveryLeadTime || "").trim() || null,
        updatedBy,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData?.message || "Customer added to e-catalogue successfully",
          { autoClose: 2000 },
        );
        setFormData({
          ...EMPTY_FORM,
          Currency: currency,
        });
        navigate("/ecatalogue");
      } else {
        toast.error(
          responseData?.message || "Failed to add customer to e-catalogue",
          { autoClose: 2000 },
        );
      }
    } catch (error) {
      console.error("Add e-catalogue customer error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        (typeof errData === "object" && errData?.message) ||
          "An error occurred while adding customer to e-catalogue",
        { autoClose: 2000 },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const header = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-6 col-sm-6">
            <h1 className="m-0 txt_lft">Add Customer to E-Catalogue</h1>
          </div>
          <div className="col-6 col-sm-6">
            <ol className="breadcrumb justify-content-end mb-0">
              <li className="breadcrumb-item">
                <Link to="/ecatalogue">
                  <i className="fa-solid fa-arrow-left-long" /> Back
                </Link>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p>E-catalogue not found</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const customerPriceRows = getCustomerPriceRows(item);
  const existingCustomerIds = new Set(
    customerPriceRows
      .map((row) => String(row.customerId || "").trim())
      .filter(Boolean),
  );
  const availableCustomers = customers.filter(
    (customer) => !existingCustomerIds.has(String(customer._id || "").trim()),
  );

  return (
    <div>
      {header}
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <div className="card-body">
                    <SectionTitle
                      icon="fa-book-open"
                      title="Catalogue Details"
                    />
                    <ViewField label="Material Code">
                      {displayValue(item.materialCode)}
                    </ViewField>
                    <ViewField label="Product Name">
                      {displayValue(item.productName)}
                    </ViewField>
                    <ViewField label="Material Description">
                      {displayValue(item.materialDescription)}
                    </ViewField>
                    <ViewField label="Category">
                      {displayValue(item.categoryName)}
                    </ViewField>
                    <ViewField label="Subcategory">
                      {displayValue(item.subcategoryName)}
                    </ViewField>
                    <ViewField label="HSN Code">
                      {displayValue(item.hsnCode)}
                    </ViewField>
                    <ViewField label="UOM">{displayValue(item.uom)}</ViewField>
                    <ViewField label="Stock Quantity">{displayValue(item.stockQuantity)}</ViewField>
                    <ViewField label="Stock Category">
                      {displayValue(item.stockCategory)}
                    </ViewField>
                    <ViewField label="Make / Brand / OEM">
                      {displayValue(item.makeBrandOem)}
                    </ViewField>
                    <ViewField label="Status">
                      {displayValue(item.status)}
                    </ViewField>

                    <SectionTitle icon="fa-users" title="Customer Details" />
                    {customerPriceRows.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">
                        No Customer Details added yet.
                      </div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "#003366",
                                color: "#fff",
                              }}
                            >
                              <th style={{ fontSize: "12px", width: "40px" }}>
                                S.No
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  minWidth: "140px",
                                  width: "150px",
                                  maxWidth: "160px",
                                }}
                              >
                                Customer
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  width: "70px",
                                  maxWidth: "75px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Currency
                              </th>
                            
                              <th
                                style={{
                                  fontSize: "12px",
                                  whiteSpace: "nowrap",
                                  minWidth: "130px",
                                  width: "140px",
                                }}
                              >
                                Customer Material Code
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  whiteSpace: "nowrap",
                                  minWidth: "240px",
                                  width: "280px",
                                  maxWidth: "320px",
                                }}
                              >
                                Customer Material Description
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  width: "80px",
                                  maxWidth: "85px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Basic Price
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  width: "80px",
                                  maxWidth: "85px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Delivery Lead Time
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  width: "55px",
                                  maxWidth: "60px",
                                  textAlign: "center",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {customerPriceRows.map((row, index) => {
                              const isEditing =
                                editingRowId === row.customerPriceId;

                              return (
                                <tr key={row.key}>
                                  <td>{index + 1}</td>
                                  <td
                                    style={{
                                      minWidth: "140px",
                                      maxWidth: "160px",
                                      width: "150px",
                                      wordWrap: "break-word",
                                      whiteSpace: "normal",
                                    }}
                                  >
                                    {row.customerName}
                                  </td>
                                  <td
                                    style={{
                                      width: "70px",
                                      maxWidth: "75px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {isEditing ? (
                                      <select
                                        className="form-control form-control-sm"
                                        name="Currency"
                                        value={editForm.Currency}
                                        onChange={handleEditFormChange}
                                        disabled={isUpdatingRow}
                                      >
                                        {CURRENCY_OPTIONS.map((opt) => (
                                          <option key={opt} value={opt}>
                                            {opt}
                                          </option>
                                        ))}
                                        {editForm.Currency &&
                                        !CURRENCY_OPTIONS.includes(
                                          editForm.Currency,
                                        ) ? (
                                          <option value={editForm.Currency}>
                                            {editForm.Currency}
                                          </option>
                                        ) : null}
                                      </select>
                                    ) : (
                                      row.currency || "-"
                                    )}
                                  </td>
                                 
                                  <td style={{ minWidth: "130px", width: "140px" }}>
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        name="customerMaterialCode"
                                        value={editForm.customerMaterialCode}
                                        onChange={handleEditFormChange}
                                        disabled={isUpdatingRow}
                                        placeholder="Customer material code"
                                      />
                                    ) : (
                                      displayValue(row.customerMaterialCode)
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      minWidth: "240px",
                                      maxWidth: "320px",
                                      width: "280px",
                                    }}
                                  >
                                    {isEditing ? (
                                      <textarea
                                        className="form-control form-control-sm"
                                        name="customerMaterialDescription"
                                        value={
                                          editForm.customerMaterialDescription
                                        }
                                        onChange={handleEditFormChange}
                                        disabled={isUpdatingRow}
                                        placeholder="Customer material description"
                                        rows={3}
                                        style={{
                                          width: "100%",
                                          resize: "vertical",
                                        }}
                                      />
                                    ) : (
                                      <span
                                        style={{
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word",
                                          display: "block",
                                          maxWidth: "320px",
                                        }}
                                      >
                                        {displayValue(
                                          row.customerMaterialDescription,
                                        )}
                                      </span>
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      fontWeight: 600,
                                      width: "80px",
                                      maxWidth: "85px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        name="price"
                                        value={editForm.price}
                                        onChange={handleEditFormChange}
                                        disabled={isUpdatingRow}
                                        min="0"
                                        step="any"
                                        placeholder="Price"
                                      />
                                    ) : (
                                      displayValue(row.price)
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      width: "110px",
                                      maxWidth: "120px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        name="deliveryLeadTime"
                                        value={editForm.deliveryLeadTime}
                                        onChange={handleEditFormChange}
                                        disabled={isUpdatingRow}
                                        placeholder="e.g. 7 days"
                                      />
                                    ) : (
                                      displayValue(row.deliveryLeadTime)
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      width: "55px",
                                      maxWidth: "60px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {isEditing ? (
                                      <div className="d-inline-flex align-items-center">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-success mr-1"
                                          onClick={() => handleSaveEditRow(row)}
                                          disabled={isUpdatingRow}
                                          title="Save"
                                          style={{ padding: "2px 8px" }}
                                        >
                                          {isUpdatingRow ? (
                                            <PulseLoader color="#fff" size={4} />
                                          ) : (
                                            <i className="fa-solid fa-check" />
                                          )}
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-secondary"
                                          onClick={handleCancelEditRow}
                                          disabled={isUpdatingRow}
                                          title="Cancel"
                                          style={{ padding: "2px 8px" }}
                                        >
                                          <i className="fa-solid fa-xmark" />
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        className="btn btn-link p-0 border-0"
                                        onClick={() => handleStartEditRow(row)}
                                        disabled={
                                          !row.customerPriceId ||
                                          isUpdatingRow ||
                                          Boolean(editingRowId)
                                        }
                                        title="Edit"
                                        style={{
                                          color: "var(--blue, #0094a9)",
                                          textDecoration: "none",
                                        }}
                                      >
                                        <i className="fa-solid fa-pen" />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle
                      icon="fa-user-plus"
                      title="Add Customer Pricing"
                    />
                    <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
                      Add another customer for this material. Customers already
                      linked to this material code are hidden and cannot be
                      selected again.
                    </p>

                    <form onSubmit={handleSubmit}>
                      <FormField label="Customer Name" required>
                        <select
                          className="form-control"
                          name="customerId"
                          value={formData.customerId}
                          onChange={handleCustomerChange}
                          disabled={isCustomersLoading || isSubmitting}
                          required
                        >
                          <option value="">
                            {isCustomersLoading
                              ? "Loading customers..."
                              : availableCustomers.length === 0
                                ? "No more customers available"
                                : "Select customer"}
                          </option>
                          {availableCustomers.map((customer) => (
                            <option key={customer._id} value={customer._id}>
                              {customer.customerName}
                            </option>
                          ))}
                        </select>
                      </FormField>

                      <FormField label="Currency" required>
                        <select
                          className="form-control"
                          name="Currency"
                          value={formData.Currency}
                          onChange={handleChange}
                          disabled={isSubmitting}
                          required
                        >
                          <option value="">Select currency</option>
                          {CURRENCY_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                          {formData.Currency &&
                          !CURRENCY_OPTIONS.includes(formData.Currency) ? (
                            <option value={formData.Currency}>
                              {formData.Currency}
                            </option>
                          ) : null}
                        </select>
                      </FormField>

                      <FormField label="Customer Material Code">
                        <input
                          type="text"
                          className="form-control"
                          name="customerMaterialCode"
                          value={formData.customerMaterialCode}
                          onChange={handleChange}
                          placeholder="e.g. LTV-A4-70"
                          disabled={isSubmitting}
                        />
                      </FormField>

                      <FormField label="Customer Material Description">
                        <textarea
                          className="form-control"
                          name="customerMaterialDescription"
                          value={formData.customerMaterialDescription}
                          onChange={handleChange}
                          placeholder="e.g. A4 70GSM JK Easy Copier"
                          disabled={isSubmitting}
                          rows={3}
                          style={{ resize: "vertical" }}
                        />
                      </FormField>

                      <FormField label="Basic Price" required>
                        <input
                          type="number"
                          className="form-control"
                          name="price"
                          value={formData.price}
                          onChange={handleChange}
                          min="0"
                          step="any"
                          placeholder="Enter price"
                          disabled={isSubmitting}
                          required
                        />
                      </FormField>
                       <FormField label="Delivery Lead Time" required>
                        <input
                          type="text"
                          className="form-control"
                          name="deliveryLeadTime"
                          value={formData.deliveryLeadTime}
                          onChange={handleChange}
                          placeholder="e.g. 7 days"
                          disabled={isSubmitting}
                          required
                        />
                      </FormField>

                      <div className="row mt-4">
                        <div className="col-12 d-flex justify-content-end">
                          <Link
                            to="/ecatalogue"
                            className="btn btn-secondary mr-2"
                          >
                            Cancel
                          </Link>
                          <button
                            type="submit"
                            className="btn btn-blue"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <PulseLoader color="#fff" size={8} />
                            ) : (
                              "Add Customer"
                            )}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EcatalogueEdit;
